import { io } from "socket.io-client";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { MutatingDots } from "react-loader-spinner";
import { useContext, useEffect, useState, KeyboardEvent} from "react";

import i18n from "@app/i18n";
import { useTranslation } from "react-i18next";


import { deviceType } from "@app/App";

import styles from "@app/assets/styles/connection.module.scss";

import { QUIZZ_SERVER_URL, QUIZZ_SOCKET_URL, API_PATH } from "@app/config";

import Context from "@app/contexts/Context";
import { GameImages } from "@app/assets/images/game/game";

import SettingsModal from "@app/components/SettingsModal";

export default function Connection() {
    const navigate = useNavigate();
      const { t } = useTranslation();

    const { setRoomId, setIoSocket, setRoomType, ioSocket, roomId } = useContext(Context);

    const [roomIdInput, setRoomIdInput] = useState("");
    const [searchRoom, setSearchRoom] = useState(false);
    const [isValidRoomId, setValidation] = useState<undefined | "wrongChar" | "notFound">();

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    // Query
    const { isLoading } = useQuery(["get", "room", roomIdInput], async () => {
        const res = await fetch(`${QUIZZ_SERVER_URL}/rooms/room-${roomIdInput}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
        });
        const room = await res.json();
        const pathApi = API_PATH ?? "";
        setRoomType(room.type);
        const socket = io(QUIZZ_SOCKET_URL, {
        path: pathApi + "/socket.io",
        transports: ["polling"],
        forceNew: true,
        withCredentials: true,
        extraHeaders: { room: room.id },
      });
        socket.connect();
        setRoomId(roomIdInput);
        setIoSocket(socket);
        setSearchRoom(false);
    }, {
        refetchOnWindowFocus: false,
        retry: false,
        refetchOnMount: false,
        enabled: !!searchRoom,
    });

    // Functions
    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Enter') {
            joinRoom();
          }
    }

    function joinRoom() {
        setSearchRoom(true);
    };

    useEffect(() => {
        if (deviceType === "cast" && roomId) {
            navigate("/lobby");
        }
    }, [roomId]);

    useEffect(() => {
        if (ioSocket) {
            ioSocket.emit("tvConnected");
            navigate("/lobby");
        }
    }, [ioSocket])

    function openSettings() {
        setIsSettingsOpen(true);
    }

    function closeSettings() {
        setIsSettingsOpen(false);
    }

    const errorText = isValidRoomId ? isValidRoomId === "notFound" ? t("connection.errors.notFound") : t("connection.errors.wrongChar") : "";

    return (
        <div className={styles.connectionContainer}>
            <img src={GameImages.lobby.url} alt=""/>
            {deviceType === "cast" ? (
                <div className={styles.waitingLaunchContainer}>
                    <div className={styles.waitingLaunchTitle}>Quizz de Génie</div>
                </div>
            ) : (
                <>
                    <div className={styles.container}>
                        {t("connection.roomId")}
                        <input
                            value={roomIdInput}
                            onKeyDown={handleKeyDown}
                            onChange={e => {
                                let isnum = e.target.value === "" || /^\d+$/.test(e.target.value);
                                if (!isnum && isValidRoomId !== "wrongChar") {
                                    setValidation("wrongChar");
                                } else if (isnum && isValidRoomId !== undefined){
                                    setValidation(undefined);
                                }
                                setRoomIdInput(e.target.value)
                            }}
                            minLength={5}
                            maxLength={5}
                            />
                        <button
                            className={styles.settingsButton}
                            type="button"
                            onClick={openSettings}
                        >
                            <img src={GameImages.settings.url} alt="settings" />
                        </button>
                        <div className={styles.error}>{errorText}</div>
                        <div className={styles.buttonWrapper}>
                            {isLoading ? (
                                <MutatingDots
                                visible={true}
                                height="100"
                                width="100"
                                color="#5478D0"
                                secondaryColor="#E4C67F"
                                radius="12.5"
                                ariaLabel="mutating-dots-loading"
                                wrapperStyle={{}}
                                wrapperClass=""
                                />
                            ) : (
                                <button onClick={joinRoom}>{t("connection.join")}</button>
                            )}
                        </div>
                    </div>
                    <SettingsModal isOpen={isSettingsOpen} closeRequest={closeSettings} onSave={(lang) => i18n.changeLanguage(lang)}/>
                </>
            )}
        </div>
    );
}
